import React, { useState, useEffect } from 'react'

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,300;0,8..60,400;0,8..60,600;1,8..60,300&family=Space+Mono:wght@400;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0f0e0c;
    --paper: #f5f0e8;
    --paper-dark: #ede7d6;
    --steel: #1a2e3b;
    --steel-mid: #3a5568;
    --accent: #1a5276;
    --accent-light: #2980b9;
    --gold: #c9943a;
    --gold-light: #f0c96e;
    --rule: #d4c9b0;
    --danger: #922b21;
  }

  .article-root {
    background: var(--paper);
    min-height: 100vh;
    font-family: 'Source Serif 4', Georgia, serif;
    color: var(--ink);
    overflow-x: hidden;
  }

  /* HEADER */
  .article-header {
    background: var(--steel);
    position: relative;
    overflow: hidden;
  }

  .header-noise {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E");
    opacity: 0.4;
    pointer-events: none;
  }

  .header-stripe {
    height: 4px;
    background: linear-gradient(90deg, var(--accent-light) 0%, var(--gold) 50%, var(--accent-light) 100%);
  }

  .header-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 48px 40px 40px;
    position: relative;
    z-index: 1;
  }

  .journal-label {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.8;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .journal-label::before, .journal-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--gold);
    opacity: 0.3;
  }

  .article-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(24px, 4vw, 44px);
    font-weight: 900;
    line-height: 1.15;
    color: #fff;
    margin-bottom: 28px;
    letter-spacing: -0.02em;
  }

  .article-title .accent-word {
    color: var(--gold-light);
    font-style: italic;
  }

  .author-block {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    border-top: 1px solid rgba(201,148,58,0.3);
  }

  .author-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-light), var(--gold));
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .author-info { display: flex; flex-direction: column; }

  .author-name {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    font-style: italic;
    color: #fff;
  }

  .author-role {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.7;
    margin-top: 2px;
  }

  /* HERO IMAGE */
  .hero-image-section {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: var(--steel);
  }

  .hero-image-section img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.7;
    filter: sepia(15%) contrast(1.15);
  }

  .hero-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 40px 16px;
    background: linear-gradient(to top, rgba(15,14,12,0.85) 0%, transparent 100%);
  }

  .hero-caption-text {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.9;
  }

  /* MAIN BODY */
  .article-body {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 40px 80px;
  }

  /* STAT ROW */
  .stat-row {
    display: flex;
    gap: 16px;
    margin: 44px 0 36px;
    flex-wrap: wrap;
  }

  .stat-card {
    flex: 1;
    min-width: 160px;
    background: var(--steel);
    padding: 22px 20px;
    position: relative;
    overflow: hidden;
  }

  .stat-card::before {
    content: '';
    position: absolute;
    bottom: -16px;
    right: -16px;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(201,148,58,0.1);
  }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 34px;
    font-weight: 900;
    color: var(--gold-light);
    line-height: 1;
    margin-bottom: 6px;
  }

  .stat-desc {
    font-family: 'Source Serif 4', serif;
    font-size: 12px;
    line-height: 1.5;
    color: rgba(255,255,255,0.75);
    font-style: italic;
  }

  /* TABLE */
  .data-table-wrap {
    margin: 24px 0 28px;
    overflow-x: auto;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  .data-table thead tr {
    background: var(--steel);
    color: #fff;
  }

  .data-table thead th {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    padding: 12px 16px;
    text-align: left;
    font-weight: 700;
  }

  .data-table tbody tr {
    border-bottom: 1px solid var(--rule);
    transition: background 0.15s;
  }

  .data-table tbody tr:hover {
    background: var(--paper-dark);
  }

  .data-table tbody td {
    padding: 11px 16px;
    color: var(--steel);
    font-weight: 300;
  }

  .data-table tbody td:first-child {
    font-weight: 600;
    color: var(--ink);
  }

  .share-bar {
    display: inline-block;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(90deg, var(--accent-light), var(--gold));
    margin-left: 10px;
    vertical-align: middle;
    opacity: 0.7;
  }

  /* ALERT BOX */
  .alert-box {
    margin: 28px 0;
    padding: 20px 24px;
    border-left: 4px solid var(--danger);
    background: rgba(146,43,33,0.06);
  }

  .alert-label {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--danger);
    font-weight: 700;
    margin-bottom: 8px;
  }

  .alert-text {
    font-size: 14.5px;
    line-height: 1.75;
    color: #2a2a2a;
    font-style: italic;
    font-weight: 300;
  }

  /* POLICY CARDS */
  .policy-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
    margin: 24px 0;
  }

  .policy-card {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 20px;
    position: relative;
  }

  .policy-num {
    font-family: 'Playfair Display', serif;
    font-size: 36px;
    font-weight: 900;
    color: var(--gold);
    opacity: 0.3;
    line-height: 1;
    margin-bottom: 8px;
  }

  .policy-title {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .policy-body {
    font-size: 13px;
    line-height: 1.65;
    color: #444;
    font-weight: 300;
  }

  /* SITUATION TABLE */
  .situation-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 14px;
  }

  .situation-table td {
    padding: 10px 14px;
    border-bottom: 1px solid var(--rule);
    vertical-align: top;
  }

  .situation-table td:first-child {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.1em;
    color: var(--accent-light);
    font-weight: 700;
    white-space: nowrap;
    width: 180px;
    padding-top: 13px;
  }

  .situation-table td:last-child {
    font-weight: 300;
    line-height: 1.7;
    color: #2a2a2a;
  }

  /* SECTION */
  .section {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid var(--rule);
    animation: fadeUp 0.5s ease both;
  }

  .section:last-child { border-bottom: none; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .section-number {
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.15em;
    color: var(--gold);
    font-weight: 700;
    margin-bottom: 6px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 2.5vw, 26px);
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 18px;
    line-height: 1.25;
    border-bottom: 2px solid var(--rule);
    padding-bottom: 10px;
  }

  .subsection-title {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    font-weight: 700;
    color: var(--steel);
    margin: 24px 0 12px;
    padding-left: 12px;
    border-left: 3px solid var(--gold);
  }

  .section-body {
    font-size: 16px;
    line-height: 1.85;
    color: #2a2a2a;
    font-weight: 300;
  }

  .section-body p { margin-bottom: 14px; }

  .section-body strong {
    color: var(--steel);
    font-weight: 600;
  }

  /* BULLET LIST */
  .bullet-list {
    list-style: none;
    margin: 14px 0 14px 8px;
    padding: 0;
  }

  .bullet-list li {
    padding: 6px 0 6px 24px;
    position: relative;
    font-size: 15.5px;
    line-height: 1.7;
    color: #2a2a2a;
    font-weight: 300;
  }

  .bullet-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15px;
    width: 10px;
    height: 2px;
    background: var(--accent-light);
  }

  /* PULL QUOTE */
  .pull-quote {
    margin: 36px -12px;
    padding: 28px 32px;
    border-top: 3px solid var(--gold);
    border-bottom: 3px solid var(--gold);
    text-align: center;
  }

  .pull-quote-text {
    font-family: 'Playfair Display', serif;
    font-size: clamp(16px, 2vw, 21px);
    font-style: italic;
    line-height: 1.6;
    color: var(--steel);
  }

  /* TOC */
  .toc-sidebar {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 24px 28px;
    margin: 44px 0;
  }

  .toc-title {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--steel-mid);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .toc-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .toc-item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: var(--steel);
    cursor: pointer;
    padding: 3px 0;
    transition: color 0.2s;
  }

  .toc-item:hover { color: var(--accent-light); }

  .toc-num {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    color: var(--gold);
    flex-shrink: 0;
  }

  /* FOOTER */
  .article-footer {
    background: var(--steel);
    padding: 32px 40px;
    text-align: center;
  }

  .footer-logo {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.6;
  }

  @media (max-width: 600px) {
    .header-inner, .article-body, .hero-caption, .article-footer { padding-left: 20px; padding-right: 20px; }
    .stat-row { flex-direction: column; }
    .hero-image-section { height: 220px; }
    .policy-grid { grid-template-columns: 1fr; }
    .situation-table td:first-child { width: 120px; }
  }
`

const supplierData = [
  { country: 'Qatar', share: '40–45%', barWidth: 90 },
  { country: 'United States', share: '15–20%', barWidth: 35 },
  { country: 'UAE', share: '7–8%', barWidth: 16 },
  { country: 'Australia', share: '6–7%', barWidth: 13 },
  { country: 'Oman & Nigeria', share: '5–6%', barWidth: 11 },
]

const policies = [
  { num: '1', title: 'Priority Allocation of Natural Gas', body: 'Develop a balanced gas allocation framework to ensure adequate and predictable supply for energy-intensive sectors such as steel.' },
  { num: '2', title: 'Long-Term LNG Procurement', body: 'Encourage long-term LNG procurement through public sector energy companies to reduce dependence on volatile spot markets.' },
  { num: '3', title: 'Diversification of Supply Sources', body: 'Expand LNG sourcing from regions such as the United States, Australia, and Africa to reduce reliance on Middle Eastern supply routes.' },
  { num: '4', title: 'Renewable Energy Integration', body: 'Promote the adoption of renewable power sources in steel manufacturing to reduce long-term dependence on imported fossil fuels.' },
  { num: '5', title: 'Energy Efficiency & Technology Upgradation', body: 'Provide incentives for the adoption of energy-efficient technologies and modern equipment in steel production.' },
  { num: '6', title: 'Strengthening Energy Infrastructure', body: 'Accelerate the development of LNG terminals, pipeline networks, and gas storage infrastructure to enhance supply resilience.' },
  { num: '7', title: 'Policy Support for MSMEs', body: 'Introduce targeted support measures to protect MSME-based steel units from extreme energy price volatility.' },
  { num: '8', title: 'Maritime Security & Diplomatic Coordination', body: 'Strengthen diplomatic engagement with Gulf nations and maintain maritime security preparedness to ensure uninterrupted energy shipments.' },
]

const tocItems = [
  { num: '1', label: 'Introduction' },
  { num: '2', label: 'Global LNG Market Developments' },
  { num: '3', label: "India's Dependence on LNG Imports" },
  { num: '4', label: 'Impact of the LNG Crisis on India' },
  { num: '5', label: 'Impact on the Indian Steel Industry' },
  { num: '6', label: "India's LNG Imports via Strait of Hormuz" },
  { num: '7', label: 'Policy Recommendations' },
  { num: '8', label: 'Conclusion' },
]

const Article2 = () => {
  useEffect(() => {}, [])

  const scrollTo = (idx) => {
    const el = document.getElementById(`section-${idx}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="article-root">
      <style>{styles}</style>

      {/* HEADER */}
      <header className="article-header">
        <div className="header-noise" />
        <div className="header-stripe" />
        <div className="header-inner">
          <div className="journal-label">AIIFA Policy Report · March 2026 · Energy & Trade Series</div>
          <h1 className="article-title">
            Global <span className="accent-word">LNG Crisis</span> and Its Impact on the Indian Steel Industry
          </h1>
          <div className="author-block">
            <div className="author-avatar">KA</div>
            <div className="author-info">
              <span className="author-name">Kamal Aggarwal</span>
              <span className="author-role">Hon. Secretary General, AIIFA</span>
            </div>
          </div>
        </div>
      </header>

      {/* HERO IMAGE */}
      <div className="hero-image-section">
        <img
          src="https://res.cloudinary.com/dxvbzgdgw/image/upload/v1774952804/WhatsApp_Image_2026-03-31_at_15.47.54_prfgbo.jpg"
          alt="LNG tanker ship at sea"
        />
        <div className="hero-caption">
          <span className="hero-caption-text">Global LNG shipping routes · Strait of Hormuz energy corridor</span>
        </div>
      </div>

      {/* BODY */}
      <main className="article-body">

        {/* STAT ROW */}
        <div className="stat-row">
          <div className="stat-card">
            <div className="stat-num">26MT</div>
            <div className="stat-desc">LNG imported by India in 2024</div>
          </div>
          <div className="stat-card">
            <div className="stat-num">65%</div>
            <div className="stat-desc">of India's LNG transits through the Strait of Hormuz</div>
          </div>
          <div className="stat-card">
            <div className="stat-num">40–45%</div>
            <div className="stat-desc">share of Qatar in India's total LNG imports</div>
          </div>
        </div>

        {/* TOC */}
        <div className="toc-sidebar">
          <div className="toc-title">Contents</div>
          <ul className="toc-list">
            {tocItems.map((item, i) => (
              <li key={i} className="toc-item" onClick={() => scrollTo(i)}>
                <span className="toc-num">{item.num}.</span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PULL QUOTE */}
        <div className="pull-quote">
          <p className="pull-quote-text">
            "The ongoing global LNG crisis has highlighted the vulnerability of energy-importing economies to geopolitical disruptions and supply volatility — with India's steel sector bearing the brunt."
          </p>
        </div>

        {/* SECTION 1 — Introduction */}
        <section id="section-0" className="section" style={{ animationDelay: '0s' }}>
          <div className="section-number">Section 01</div>
          <h2 className="section-title">Introduction</h2>
          <div className="section-body">
            <p>The Indian steel industry plays a vital role in the country's economic development by supporting infrastructure expansion, industrial growth, and large-scale employment generation. India has emerged as the world's <strong>second-largest steel producer</strong>, with a significant share of production originating from the secondary steel sector. This segment includes induction furnace units, re-rolling mills, and a wide network of micro, small, and medium enterprises (MSMEs), which collectively form the backbone of decentralized steel manufacturing and contribute substantially to regional industrial development.</p>
            <p>Energy availability and affordability remain critical determinants of the steel sector's competitiveness and long-term sustainability. Steel production processes—particularly melting, heating, and rolling—are highly energy-intensive. Consequently, even moderate fluctuations in energy prices or disruptions in fuel supply can significantly affect production costs, operational efficiency, and market competitiveness.</p>
            <p>In recent years, the global energy landscape has experienced significant volatility, particularly in the market for <strong>Liquefied Natural Gas (LNG)</strong>. The ongoing global LNG crisis—triggered by geopolitical conflicts, supply disruptions, and rising international demand—has led to sharp price fluctuations and supply uncertainties. This report examines the evolving global LNG scenario, assesses its implications for India's energy security and steel industry, and outlines key policy measures to ensure stable, affordable, and sustainable energy availability for the sector.</p>
          </div>
        </section>

        {/* SECTION 2 — Global LNG Market */}
        <section id="section-1" className="section" style={{ animationDelay: '0.05s' }}>
          <div className="section-number">Section 02</div>
          <h2 className="section-title">Global LNG Market Developments</h2>
          <div className="section-body">
            <p>The global LNG market has undergone significant changes in recent years, marked by geopolitical tensions, supply constraints, and shifting international trade dynamics. These factors have created a volatile environment for energy-importing nations such as India.</p>

            <h3 className="subsection-title">2.1 Impact of the Russia–Ukraine Conflict</h3>
            <p>One of the most significant disruptions in global energy markets followed the Russia–Ukraine conflict, which drastically altered international gas trade flows. Prior to the conflict, Europe depended heavily on pipeline gas imports from Russia. However, the conflict led to a sharp decline in Russian gas supplies, compelling European countries to seek alternative sources of energy, particularly LNG from global markets.</p>
            <p>As a result:</p>
            <ul className="bullet-list">
              <li>European countries significantly increased LNG imports to compensate for reduced pipeline gas supplies.</li>
              <li>Global demand for LNG rose sharply, intensifying competition among importing countries.</li>
              <li>Asian markets, including India, faced increased competition for LNG cargoes.</li>
            </ul>
            <p>This shift in global demand patterns caused LNG spot prices to rise dramatically and reduced the availability of cargoes for price-sensitive economies.</p>

            <h3 className="subsection-title">2.2 Supply Constraints in the LNG Market</h3>
            <p>While global demand for LNG increased rapidly, supply expansion remained limited due to several structural challenges, including:</p>
            <ul className="bullet-list">
              <li>Delays in commissioning new LNG liquefaction projects</li>
              <li>Periodic maintenance shutdowns and operational disruptions at existing LNG facilities</li>
              <li>Feed-gas supply limitations in major exporting regions</li>
            </ul>
            <p>These factors have resulted in a relatively tight LNG market, making prices highly sensitive to geopolitical developments and sudden shifts in demand.</p>

            <h3 className="subsection-title">2.3 Geopolitical Risks in Energy Supply Routes</h3>
            <p>Global LNG trade also faces potential disruptions from geopolitical tensions affecting key maritime energy corridors. One such critical route is the <strong>Strait of Hormuz</strong>, through which a substantial portion of the world's oil and LNG shipments pass. Any disruption in this strategic shipping lane could significantly affect global energy supplies. Given that a large share of India's LNG imports originates from Middle Eastern countries, instability in this region poses a direct risk to India's energy security.</p>
          </div>
        </section>

        {/* SECTION 3 — India's LNG Dependence */}
        <section id="section-2" className="section" style={{ animationDelay: '0.1s' }}>
          <div className="section-number">Section 03</div>
          <h2 className="section-title">India's Dependence on LNG Imports</h2>
          <div className="section-body">
            <p>India has emerged as one of the world's major LNG importers, driven by increasing demand for natural gas across multiple sectors such as fertilizers, power generation, city gas distribution, and industrial applications. In 2024, India imported approximately <strong>26 million tonnes of LNG</strong>, reflecting the growing role of natural gas in the country's evolving energy mix.</p>
            <p>India sources LNG from several supplier countries, although a significant portion of imports continues to originate from the Middle East.</p>

            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Supplier Country</th>
                    <th>Share of Imports</th>
                    <th>Relative Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierData.map((row, i) => (
                    <tr key={i}>
                      <td>{row.country}</td>
                      <td>{row.share}</td>
                      <td>
                        <span className="share-bar" style={{ width: `${row.barWidth}px` }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p>Despite ongoing efforts to diversify supply sources, India remains substantially dependent on LNG imports from the Middle East, making it vulnerable to geopolitical developments in the region.</p>
          </div>
        </section>

        {/* SECTION 4 — Impact on India */}
        <section id="section-3" className="section" style={{ animationDelay: '0.15s' }}>
          <div className="section-number">Section 04</div>
          <h2 className="section-title">Impact of the LNG Crisis on India</h2>
          <div className="section-body">
            <h3 className="subsection-title">4.1 Surge in LNG Prices</h3>
            <p>The global LNG crisis has led to a sharp rise in international gas prices. During peak periods of volatility, LNG spot prices surged several times above historical averages. For price-sensitive markets such as India, this resulted in:</p>
            <ul className="bullet-list">
              <li>Reduced LNG imports during periods of extremely high prices</li>
              <li>Increased energy costs for industries and utilities</li>
              <li>Greater reliance on alternative fuels, particularly coal</li>
            </ul>

            <h3 className="subsection-title">4.2 Limited Gas Availability for Industry</h3>
            <p>Due to supply constraints and competing demand from priority sectors such as fertilizers and city gas distribution, industrial access to natural gas has at times been restricted. Industries that rely on gas-based heating processes or gas-based power generation have been particularly affected.</p>
          </div>
        </section>

        {/* SECTION 5 — Impact on Steel Industry */}
        <section id="section-4" className="section" style={{ animationDelay: '0.2s' }}>
          <div className="section-number">Section 05</div>
          <h2 className="section-title">Impact on the Indian Steel Industry</h2>
          <div className="section-body">
            <p>The steel industry is among the most energy-intensive sectors of the economy. Energy costs represent a substantial share of total production expenditure, making the industry particularly vulnerable to fluctuations in fuel prices.</p>

            <h3 className="subsection-title">5.1 Rising Production Costs</h3>
            <p>The increase in LNG prices has contributed to higher electricity tariffs and fuel costs—both of which are critical inputs for steel manufacturing. As a result, production costs have risen significantly, placing pressure on profit margins and increasing financial stress for steel producers.</p>

            <h3 className="subsection-title">5.2 Vulnerability of the Secondary Steel Sector</h3>
            <p>India's secondary steel sector—dominated by MSME-based induction furnace units and rolling mills—is particularly sensitive to energy price volatility. These enterprises generally operate with limited financial reserves and depend heavily on stable and affordable energy supply. Consequently, fluctuations in energy prices have led to:</p>
            <ul className="bullet-list">
              <li>Reduced capacity utilization</li>
              <li>Increased operational costs</li>
              <li>Temporary production disruptions in certain units</li>
            </ul>

            <h3 className="subsection-title">5.3 Challenges in Industrial Clusters</h3>
            <p>Steel manufacturing clusters across several regions in India have reported operational challenges due to rising energy costs and limited availability of gas. Such disruptions have broader economic implications, including:</p>
            <ul className="bullet-list">
              <li>Reduced industrial output</li>
              <li>Financial stress among small and medium enterprises</li>
              <li>Potential employment impacts in steel-producing regions</li>
            </ul>
          </div>
        </section>

        {/* SECTION 6 — Strait of Hormuz */}
        <section id="section-5" className="section" style={{ animationDelay: '0.25s' }}>
          <div className="section-number">Section 06</div>
          <h2 className="section-title">India's LNG Imports via the Strait of Hormuz</h2>
          <div className="section-body">
            <h3 className="subsection-title">6.1 Strategic Significance</h3>
            <p>The <strong>Strait of Hormuz</strong> is the world's most important energy transit chokepoint, linking the Persian Gulf with global energy markets. Nearly <strong>65 percent of India's LNG imports transit through this route</strong>, particularly shipments from Qatar and the UAE. Recent geopolitical tensions in the Gulf region have heightened concerns regarding potential disruptions to LNG shipments passing through this corridor.</p>

            <h3 className="subsection-title">6.2 Current Situation (March 2026)</h3>
            <table className="situation-table">
              <tbody>
                <tr>
                  <td>Legal Status</td>
                  <td>India retains the right of passage for LNG shipments under international maritime law.</td>
                </tr>
                <tr>
                  <td>Operational Reality</td>
                  <td>LNG carriers continue to transit the Strait, although with heightened risk awareness and security monitoring.</td>
                </tr>
                <tr>
                  <td>Diplomatic Engagement</td>
                  <td>India is actively engaging with Iran and Gulf nations to ensure uninterrupted energy trade.</td>
                </tr>
                <tr>
                  <td>Security Preparedness</td>
                  <td>Naval contingency measures, including possible escort arrangements, are being considered to protect critical energy shipments.</td>
                </tr>
              </tbody>
            </table>

            <h3 className="subsection-title">6.3 Risks and Vulnerabilities</h3>
            <div className="alert-box">
              <div className="alert-label">⚠ Risk Assessment</div>
              <p className="alert-text">Despite diplomatic and security measures, geopolitical tensions could disrupt shipping routes. Nearly two-thirds of India's LNG imports depend on this single chokepoint, while price volatility and forex risks place additional pressure on MSMEs and energy-intensive industries. Limited domestic regasification infrastructure further restricts flexibility in sourcing alternative supplies.</p>
            </div>

            <h3 className="subsection-title">6.4 Strategic Implications</h3>
            <p>These vulnerabilities underscore the need for strengthening India's energy security through diversification of supply sources and expansion of domestic energy infrastructure. Increasing LNG imports from countries such as the United States, Australia, and emerging African exporters could help reduce excessive dependence on the Strait of Hormuz.</p>
          </div>
        </section>

        {/* SECTION 7 — Policy Recommendations */}
        <section id="section-6" className="section" style={{ animationDelay: '0.3s' }}>
          <div className="section-number">Section 07</div>
          <h2 className="section-title">Policy Recommendations</h2>
          <div className="section-body">
            <p>To mitigate the impact of the global LNG crisis and strengthen the resilience of the Indian steel industry, the following policy measures may be considered:</p>
            <div className="policy-grid">
              {policies.map((p, i) => (
                <div className="policy-card" key={i}>
                  <div className="policy-num">{p.num}</div>
                  <div className="policy-title">{p.title}</div>
                  <div className="policy-body">{p.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 8 — Conclusion */}
        <section id="section-7" className="section" style={{ animationDelay: '0.35s' }}>
          <div className="section-number">Section 08</div>
          <h2 className="section-title">Conclusion</h2>
          <div className="section-body">
            <p>The ongoing global LNG crisis has highlighted the vulnerability of energy-importing economies to geopolitical disruptions and supply volatility. For India's steel sector—particularly the MSME-dominated secondary steel industry—the crisis has translated into rising energy costs, operational uncertainty, and reduced competitiveness.</p>
            <p>Ensuring reliable, affordable, and sustainable energy supply is therefore essential for sustaining the growth momentum of the Indian steel industry. Strategic policy interventions aimed at diversifying energy sources, strengthening supply infrastructure, promoting energy efficiency, and supporting MSME-based units will significantly enhance the sector's resilience.</p>
            <p>The industry respectfully urges the Government to consider the above recommendations and extend appropriate policy support to safeguard the interests of the steel sector and ensure its continued contribution to India's <strong>industrial and economic development</strong>.</p>
          </div>
        </section>

      </main>
    </div>
  )
}

export default Article2